/**
 * HelpLauncher. The help button, wherever help queries live.
 *
 * With LoopDesk connected (the backend answers /api/v1/loopdesk/me/), the LoopDesk
 * request button and panel take over: queries, screenshots, replies, "It works" /
 * "Still broken" and What's new, all answered by the LoopDesk team. Without it, the
 * built-in SupportLauncher stays exactly as before.
 */

import { LoopDeskLauncher, openLoopDesk, type LoopDeskTheme } from "loopdesk-widget";
import "loopdesk-widget/styles.css";
import { useEffect } from "react";

import { LOOPDESK_BASE, loopdeskFetch, useLoopDesk } from "@/lib/api/loopdesk";
import { useFeature } from "@/lib/tenant";

import { OPEN_SUPPORT_EVENT } from "./supportFormat";
import { SupportLauncher } from "./SupportLauncher";

export function HelpLauncher() {
  const supportOn = useFeature("loopdesk_support");
  const { state } = useLoopDesk(supportOn);

  // openSupport() from anywhere (the Changelog page, shortcuts) opens LoopDesk's panel.
  useEffect(() => {
    if (state !== "on") return;
    function onOpen(event: Event) {
      const detail = (event as CustomEvent<{ number?: string } | undefined>).detail;
      openLoopDesk(detail?.number ? { number: detail.number } : { view: "new" });
    }
    window.addEventListener(OPEN_SUPPORT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SUPPORT_EVENT, onOpen);
  }, [state]);

  if (!supportOn || state === "loading") return null;
  if (state === "legacy") return <SupportLauncher />;
  return <LoopDeskLauncher apiBase={LOOPDESK_BASE} fetch={loopdeskFetch} getHeaders={noHeaders} theme={appTheme} />;
}

const noHeaders = () => ({});

/**
 * The LoopDesk button and panel in the app's look: deep teal accent, cool cream surfaces,
 * Plus Jakarta Sans, the app radii. Every value is a theme token, so it follows
 * [data-theme="dark"] on its own (styles/app.css).
 */
const appTheme: LoopDeskTheme = {
  accent: "var(--color-accent)",
  accentHover: "color-mix(in srgb, var(--color-accent) 88%, white)",
  accentPress: "color-mix(in srgb, var(--color-accent) 85%, black)",
  accentSoft: "var(--color-accent-soft)",
  accentRing: "color-mix(in srgb, var(--color-border-focus) 35%, transparent)",
  onAccent: "var(--color-on-accent)",
  team: "var(--color-link)",
  teamSoft: "var(--color-surface-teal)",
  bg: "var(--color-surface)",
  bgBase: "var(--color-page)",
  bgSunken: "var(--color-surface-alt)",
  line: "var(--color-border-default)",
  lineStrong: "var(--color-border-emphasis)",
  text: "var(--color-secondary)",
  textStrong: "var(--color-primary)",
  muted: "var(--color-muted)",
  faint: "var(--color-faint)",
  green: "var(--color-success)",
  red: "var(--color-danger)",
  amber: "var(--color-warning-ink)",
  blue: "var(--color-info)",
  radiusSm: "var(--radius-sm)",
  radius: "var(--radius-md)",
  radiusLg: "var(--radius-xl)",
  font: "var(--font-sans)",
  shadowOverlay: "var(--shadow-modal, var(--shadow-lg))",
};
